/**
 * Calendar Main Page
 *
 * Coordinator component that composes toolbar, sidebar, view area, and all
 * dialog components into the complete calendar experience.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { toast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import type { SchedulableTaskDetail, AiScheduleSuggestion } from '@/lib/api';
import { useArcheSettings } from '@/lib/hooks/useArcheSettings';
import { ARCHE_ID } from '../constants';

import type {
  CalendarView,
  CalendarSettings,
  CalendarEventInput,
  EventInstance,
  RecurrenceEditScope,
  RecurrenceRule,
} from '../types';
import { DEFAULT_CALENDAR_SETTINGS, getHourHeight } from '../types';
import { navigateDate } from '../utils';

import { useCalendars } from '../hooks/useCalendars';
import { useEvents } from '../hooks/useEvents';
import { useVisibleRange } from '../hooks/useVisibleRange';
import { useCalendarShortcuts } from '../hooks/useCalendarShortcuts';
import { useCalendarSets } from '../hooks/useCalendarSets';
import { useEventTemplates } from '../hooks/useEventTemplates';
import { useSharedCalendars } from '../hooks/useCalendarSharing';
import { useSchedulingLinks } from '../hooks/useSchedulingLinks';
import { useSchedulableTasks } from '../hooks/useSchedulableTasks';
import { useFocusTime } from '../hooks/useFocusTime';
import { useDailyPlanning } from '../hooks/useDailyPlanning';
import { useCalendarSubscriptions } from '../hooks/useCalendarSubscriptions';
import { useEventSearch } from '../hooks/useEventSearch';
import { useEventResize } from '../hooks/useEventResize';
import { useWeather } from '../hooks/useWeather';

import { CalendarToolbar } from '../components/CalendarToolbar';
import { CalendarSidebar } from '../components/CalendarSidebar';
import { CalendarViewArea } from '../components/CalendarViewArea';
import { CalendarDialogs } from '../components/CalendarDialogs';
import { TaskPanel } from '../components/TaskPanel';
import type { EventDialogSettings } from '../components/EventDialog';

export function CalendarMainPage() {
  const [settings, updateSettings] = useArcheSettings<CalendarSettings>(
    ARCHE_ID,
    DEFAULT_CALENDAR_SETTINGS,
  );

  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>(settings.defaultView);

  // Data
  const {
    calendars,
    loading: calendarsLoading,
    error: calendarsError,
    fetchCalendars,
    createCalendar,
    deleteCalendar,
    toggleVisibility,
    visibleCalendarIds,
  } = useCalendars();

  const range = useVisibleRange(currentDate, view, settings);
  const {
    events,
    loading: eventsLoading,
    refetch: refetchEvents,
  } = useEvents(range.start, range.end);

  // Fixed date ranges for daily planning (independent of navigation)
  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const todayEnd = useMemo(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);
  const yesterdayStart = useMemo(() => {
    const d = new Date(todayStart);
    d.setDate(d.getDate() - 1);
    return d;
  }, [todayStart]);
  const yesterdayEnd = useMemo(() => {
    const d = new Date(todayEnd);
    d.setDate(d.getDate() - 1);
    return d;
  }, [todayEnd]);

  const { events: todayEventsRaw } = useEvents(todayStart, todayEnd);
  const { events: yesterdayEventsRaw } = useEvents(yesterdayStart, yesterdayEnd);

  // Calendar Sets
  const {
    calendarSets,
    activeCalendarSetId,
    createSet,
    renameSet,
    deleteSet: deleteCalendarSet,
    updateSetCalendars,
    activateSet,
    deactivateSet,
  } = useCalendarSets({
    settings,
    updateSettings,
    calendars,
    toggleVisibility,
  });

  // Event Templates
  const { templates, createTemplate, updateTemplate, deleteTemplate, createTemplateFromEvent } =
    useEventTemplates();

  // Shared Calendars
  const { sharedCalendars } = useSharedCalendars();

  // Scheduling Links
  const {
    schedulingLinks,
    createLink: createSchedulingLink,
    updateLink: updateSchedulingLink,
    deleteLink: deleteSchedulingLink,
    fetchBookings: fetchSchedulingLinkBookings,
    cancelBooking: cancelSchedulingLinkBooking,
  } = useSchedulingLinks();

  // Schedulable Tasks
  const { tasks, createTask, updateTask, suggestSchedule, acceptSuggestion, completeTask } =
    useSchedulableTasks();

  // Focus Time
  const {
    settings: focusTimeSettings,
    updateSettings: updateFocusTimeSettings,
    generate: generateFocusBlocks,
    regenerate: regenerateFocusBlocks,
    clear: clearFocusBlocks,
  } = useFocusTime();

  // Calendar Subscriptions
  const {
    subscriptions,
    subscribe: subscribeToFeed,
    refresh: refreshSubscription,
    unsubscribe,
  } = useCalendarSubscriptions();

  // Event Search
  const search = useEventSearch();

  const handleSearchSelect = useCallback((event: import('@/lib/api').CalendarEventInstance) => {
    setCurrentDate(new Date(event.startAt));
    setSelectedEvent(event);
    setDetailDialogOpen(true);
  }, []);

  // Event Resize
  const handleResizeEvent = useCallback(
    async (eventId: string, newDurationMinutes: number) => {
      const event = events.find((e) => e.eventId === eventId);
      if (!event) return;
      const startAt = new Date(event.startAt);
      const newEndAt = new Date(startAt.getTime() + newDurationMinutes * 60 * 1000);
      try {
        await api.updateCalendarEvent(eventId, {
          endAt: newEndAt.toISOString(),
          scope: 'this',
          instanceDate: event.originalStartAt,
        });
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to resize event',
          variant: 'destructive',
        });
      }
    },
    [events, refetchEvents],
  );

  const hourHeightPx = getHourHeight(settings.timeGridDensity);
  const { resizingEventId, resizeHeight, onResizeStart } = useEventResize(
    handleResizeEvent,
    hourHeightPx,
    settings.snapIntervalMinutes,
  );

  // Weather
  const weather = useWeather(settings.showWeather ?? true);

  // Daily Planning
  const dailyPlanning = useDailyPlanning({
    settings,
    updateSettings,
    todayEvents: todayEventsRaw,
    yesterdayEvents: yesterdayEventsRaw,
    tasks,
  });

  const [dailyPlanningOpen, setDailyPlanningOpen] = useState(false);
  const [eveningReviewOpen, setEveningReviewOpen] = useState(false);
  const [focusTimeDialogOpen, setFocusTimeDialogOpen] = useState(false);

  // Auto-trigger morning wizard (800ms delay after data loads)
  const morningWizardTriggered = useRef(false);
  useEffect(() => {
    if (
      dailyPlanning.shouldShowMorningWizard &&
      !morningWizardTriggered.current &&
      !calendarsLoading &&
      !eventsLoading
    ) {
      morningWizardTriggered.current = true;
      const timer = setTimeout(() => setDailyPlanningOpen(true), 800);
      return () => clearTimeout(timer);
    }
  }, [dailyPlanning.shouldShowMorningWizard, calendarsLoading, eventsLoading]);

  // Auto-trigger evening review (800ms delay after data loads)
  const eveningReviewTriggered = useRef(false);
  useEffect(() => {
    if (
      dailyPlanning.shouldShowEveningReview &&
      !eveningReviewTriggered.current &&
      !calendarsLoading &&
      !eventsLoading
    ) {
      eveningReviewTriggered.current = true;
      const timer = setTimeout(() => setEveningReviewOpen(true), 800);
      return () => clearTimeout(timer);
    }
  }, [dailyPlanning.shouldShowEveningReview, calendarsLoading, eventsLoading]);

  // Filter events by visible calendars
  const visibleEvents = useMemo(
    () => events.filter((e) => visibleCalendarIds.includes(e.calendarId)),
    [events, visibleCalendarIds],
  );

  // Dialog state
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [quickEventOpen, setQuickEventOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [calendarFormOpen, setCalendarFormOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);
  const [calendarSetManagerOpen, setCalendarSetManagerOpen] = useState(false);
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareCalendarTarget, setShareCalendarTarget] = useState<string | null>(null);
  const [findTimeOpen, setFindTimeOpen] = useState(false);
  const [schedulingLinksOpen, setSchedulingLinksOpen] = useState(false);
  const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);

  // Task dialog state
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editTaskTarget, setEditTaskTarget] = useState<SchedulableTaskDetail | null>(null);
  const [suggestionsDialogOpen, setSuggestionsDialogOpen] = useState(false);
  const [suggestionsTask, setSuggestionsTask] = useState<SchedulableTaskDetail | null>(null);
  const [suggestions, setSuggestions] = useState<AiScheduleSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const [selectedEvent, setSelectedEvent] = useState<EventInstance | null>(null);
  const [editEvent, setEditEvent] = useState<EventInstance | null>(null);
  const [quickEventDate, setQuickEventDate] = useState(new Date());
  const [quickEventHour, setQuickEventHour] = useState(9);
  const [initialEventData, setInitialEventData] = useState<Partial<CalendarEventInput>>({});

  // Navigation
  const handlePrev = useCallback(() => {
    setCurrentDate((d) => navigateDate(d, view, 'prev'));
  }, [view]);

  const handleNext = useCallback(() => {
    setCurrentDate((d) => navigateDate(d, view, 'next'));
  }, [view]);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const handleViewChange = useCallback((v: CalendarView) => {
    setView(v);
  }, []);

  const handleGoToDate = useCallback((date: Date) => {
    setCurrentDate(date);
  }, []);

  // Event interactions
  const handleClickEvent = useCallback((event: EventInstance) => {
    setSelectedEvent(event);
    setDetailDialogOpen(true);
  }, []);

  const handleClickSlot = useCallback((date: Date, hour: number) => {
    setQuickEventDate(date);
    setQuickEventHour(hour);
    setQuickEventOpen(true);
  }, []);

  const handleClickDate = useCallback((date: Date) => {
    setCurrentDate(date);
    setView('day');
  }, []);

  const handleNewEvent = useCallback(() => {
    setEditEvent(null);
    setInitialEventData({});
    setEventDialogOpen(true);
  }, []);

  // CRUD operations
  const handleSaveEvent = useCallback(
    async (input: CalendarEventInput & { rruleObj?: RecurrenceRule | null }) => {
      const { rruleObj: _rruleObj, ...eventInput } = input;

      try {
        if (editEvent) {
          await api.updateCalendarEvent(editEvent.eventId, {
            ...eventInput,
            scope: 'all',
          });
          toast({ title: 'Event updated' });
        } else {
          await api.createCalendarEvent(eventInput);
          toast({ title: 'Event created' });
        }
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to save event',
          variant: 'destructive',
        });
      }
    },
    [editEvent, refetchEvents],
  );

  const handleQuickSave = useCallback(
    async (title: string) => {
      const startAt = new Date(quickEventDate);
      startAt.setHours(quickEventHour, 0, 0, 0);
      const endAt = new Date(startAt.getTime() + settings.defaultEventDuration * 60000);

      const defaultCalendar = calendars.find((c) => c.isDefault) || calendars[0];
      if (!defaultCalendar) {
        toast({ title: 'No calendar available', variant: 'destructive' });
        return;
      }

      try {
        await api.createCalendarEvent({
          calendarId: defaultCalendar.id,
          title,
          startAt: startAt.toISOString(),
          endAt: endAt.toISOString(),
          status: settings.defaultEventStatus,
          color: settings.defaultEventColor,
          reminders:
            settings.defaultEventReminder != null
              ? [{ type: 'notification', minutesBefore: settings.defaultEventReminder }]
              : undefined,
        });
        toast({ title: 'Event created' });
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to create event',
          variant: 'destructive',
        });
      }
    },
    [
      quickEventDate,
      quickEventHour,
      calendars,
      refetchEvents,
      settings.defaultEventDuration,
      settings.defaultEventStatus,
      settings.defaultEventColor,
      settings.defaultEventReminder,
    ],
  );

  const handleEditEvent = useCallback((event: EventInstance) => {
    setEditEvent(event);
    setEventDialogOpen(true);
  }, []);

  const handleDeleteEvent = useCallback(
    async (event: EventInstance, scope: RecurrenceEditScope) => {
      try {
        await api.deleteCalendarEvent(event.eventId, scope, event.originalStartAt);
        toast({ title: 'Event deleted' });
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to delete',
          variant: 'destructive',
        });
      }
    },
    [refetchEvents],
  );

  const handleDeleteSearchEvents = useCallback(
    async (eventIds: string[]) => {
      let deleted = 0;
      for (const id of eventIds) {
        try {
          await api.deleteCalendarEvent(id, 'all');
          deleted++;
        } catch {
          // continue deleting remaining
        }
      }
      refetchEvents();
      search.setQuery('');
      toast({
        title: `Deleted ${deleted} event${deleted === 1 ? '' : 's'}`,
        ...(deleted < eventIds.length && {
          description: `${eventIds.length - deleted} failed to delete`,
          variant: 'destructive' as const,
        }),
      });
    },
    [refetchEvents, search],
  );

  const handleSaveCalendar = useCallback(
    async (input: { name: string; description?: string; color?: string }) => {
      await createCalendar(input);
      toast({ title: 'Calendar created' });
    },
    [createCalendar],
  );

  const handleDeleteCalendar = useCallback(
    async (id: string) => {
      try {
        await deleteCalendar(id);
        toast({ title: 'Calendar deleted' });
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to delete calendar',
          variant: 'destructive',
        });
      }
    },
    [deleteCalendar, refetchEvents],
  );

  const handleSubscribe = useCallback(
    async (feedUrl: string, name?: string) => {
      try {
        await subscribeToFeed(feedUrl, name);
        toast({ title: 'Subscribed to feed' });
        fetchCalendars();
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to subscribe',
          variant: 'destructive',
        });
        throw err;
      }
    },
    [subscribeToFeed, fetchCalendars, refetchEvents],
  );

  const handleRefreshSubscription = useCallback(
    async (id: string) => {
      try {
        await refreshSubscription(id);
        toast({ title: 'Subscription refreshed' });
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to refresh',
          variant: 'destructive',
        });
      }
    },
    [refreshSubscription, refetchEvents],
  );

  const handleUnsubscribe = useCallback(
    async (id: string) => {
      try {
        await unsubscribe(id);
        toast({ title: 'Unsubscribed' });
        fetchCalendars();
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to unsubscribe',
          variant: 'destructive',
        });
      }
    },
    [unsubscribe, fetchCalendars, refetchEvents],
  );

  const handleShareCalendar = useCallback((calendarId: string) => {
    setShareCalendarTarget(calendarId);
    setShareDialogOpen(true);
  }, []);

  const handleFindTimeSelectSlot = useCallback((startAt: string, endAt: string) => {
    setInitialEventData({ startAt, endAt });
    setEditEvent(null);
    setEventDialogOpen(true);
  }, []);

  const handleQuickExpand = useCallback(
    (parsedData?: Partial<CalendarEventInput>) => {
      setQuickEventOpen(false);
      const startAt = new Date(quickEventDate);
      startAt.setHours(quickEventHour, 0, 0, 0);
      const endAt = new Date(startAt.getTime() + settings.defaultEventDuration * 60000);
      setInitialEventData({
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        ...parsedData,
      });
      setEditEvent(null);
      setEventDialogOpen(true);
    },
    [quickEventDate, quickEventHour, settings.defaultEventDuration],
  );

  const handleSaveAsTemplate = useCallback(
    async (name: string) => {
      if (!editEvent) return;
      try {
        await createTemplateFromEvent(editEvent, name);
        toast({ title: 'Template saved' });
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to save template',
          variant: 'destructive',
        });
      }
    },
    [editEvent, createTemplateFromEvent],
  );

  // Task handlers
  const handleAutoSchedule = useCallback(
    async (task: SchedulableTaskDetail) => {
      setSuggestionsTask(task);
      setSuggestions([]);
      setSuggestionsDialogOpen(true);
      setSuggestionsLoading(true);
      try {
        const result = await suggestSchedule(task.id, {
          workingHoursStart: settings.workingHoursStart,
          workingHoursEnd: settings.workingHoursEnd,
          timezone:
            settings.timezone === 'local'
              ? Intl.DateTimeFormat().resolvedOptions().timeZone
              : settings.timezone,
        });
        setSuggestions(result);
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to get suggestions',
          variant: 'destructive',
        });
      } finally {
        setSuggestionsLoading(false);
      }
    },
    [suggestSchedule, settings],
  );

  const handleAcceptSuggestion = useCallback(
    async (index: number) => {
      if (!suggestionsTask) return;
      try {
        await acceptSuggestion(suggestionsTask.id, index);
        toast({ title: 'Task scheduled' });
        setSuggestionsDialogOpen(false);
        refetchEvents();
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to accept suggestion',
          variant: 'destructive',
        });
      }
    },
    [suggestionsTask, acceptSuggestion, refetchEvents],
  );

  const handleCompleteTask = useCallback(
    async (taskId: string) => {
      try {
        await completeTask(taskId);
        toast({ title: 'Task completed' });
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to complete task',
          variant: 'destructive',
        });
      }
    },
    [completeTask],
  );

  const handleClickTask = useCallback((task: SchedulableTaskDetail) => {
    setEditTaskTarget(task);
    setTaskDialogOpen(true);
  }, []);

  const handleSaveTask = useCallback(
    async (input: import('@/lib/api').SchedulableTaskInput) => {
      try {
        if (editTaskTarget) {
          await updateTask(editTaskTarget.id, input);
          toast({ title: 'Task updated' });
        } else {
          await createTask(input);
          toast({ title: 'Task created' });
        }
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to save task',
          variant: 'destructive',
        });
        throw err;
      }
    },
    [editTaskTarget, createTask, updateTask],
  );

  const handleToggleTaskPanel = useCallback(() => {
    updateSettings({ showTaskPanel: !settings.showTaskPanel });
  }, [settings.showTaskPanel, updateSettings]);

  const handleFocusBlocksAction = useCallback(
    async (action: () => Promise<number>) => {
      const result = await action();
      refetchEvents();
      return result;
    },
    [refetchEvents],
  );

  const handleScheduleTaskToTime = useCallback(
    async (taskId: string, hour: number, durationMinutes: number) => {
      const defaultCalendar = calendars.find((c) => c.isDefault) || calendars[0];
      if (!defaultCalendar) return;

      const task = tasks.find((t) => t.id === taskId);
      if (!task) return;

      const startAt = new Date();
      startAt.setHours(hour, 0, 0, 0);
      const endAt = new Date(startAt.getTime() + durationMinutes * 60000);

      try {
        await api.createCalendarEvent({
          calendarId: defaultCalendar.id,
          title: task.title,
          description: task.description || undefined,
          startAt: startAt.toISOString(),
          endAt: endAt.toISOString(),
        });
        refetchEvents();
        toast({ title: 'Task scheduled' });
      } catch (err) {
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to schedule task',
          variant: 'destructive',
        });
      }
    },
    [calendars, tasks, refetchEvents],
  );

  // Keyboard shortcuts -- placed after all handlers so refs capture latest values
  const shortcutActionsRef = useRef({
    onNewEvent: handleNewEvent,
    onToday: handleToday,
    onPrev: handlePrev,
    onNext: handleNext,
    onViewChange: handleViewChange,
    onOpenCommandPalette: () => setCommandPaletteOpen(true),
    onOpenShortcutHelp: () => setShortcutHelpOpen(true),
  });
  shortcutActionsRef.current = {
    onNewEvent: handleNewEvent,
    onToday: handleToday,
    onPrev: handlePrev,
    onNext: handleNext,
    onViewChange: handleViewChange,
    onOpenCommandPalette: () => setCommandPaletteOpen(true),
    onOpenShortcutHelp: () => setShortcutHelpOpen(true),
  };

  const stableShortcutActions = useMemo(
    () => ({
      onNewEvent: () => shortcutActionsRef.current.onNewEvent(),
      onToday: () => shortcutActionsRef.current.onToday(),
      onPrev: () => shortcutActionsRef.current.onPrev(),
      onNext: () => shortcutActionsRef.current.onNext(),
      onViewChange: (v: CalendarView) => shortcutActionsRef.current.onViewChange(v),
      onOpenCommandPalette: () => shortcutActionsRef.current.onOpenCommandPalette(),
      onOpenShortcutHelp: () => shortcutActionsRef.current.onOpenShortcutHelp(),
    }),
    [],
  );
  useCalendarShortcuts(stableShortcutActions);

  // Meeting count warning for toolbar
  const meetingWarning = useMemo(() => {
    if (settings.maxMeetingsPerDay == null) return null;
    const todayCount = todayEventsRaw.filter((e) => !e.isAllDay).length;
    if (todayCount >= settings.maxMeetingsPerDay) {
      return { count: todayCount, max: settings.maxMeetingsPerDay };
    }
    return null;
  }, [todayEventsRaw, settings.maxMeetingsPerDay]);

  const loading = calendarsLoading || eventsLoading;

  // Grouped settings for EventDialog (I11)
  const eventDialogSettings: EventDialogSettings = useMemo(
    () => ({
      defaultCalendarId: settings.defaultCalendarId || calendars[0]?.id || null,
      defaultEventDuration: settings.defaultEventDuration,
      defaultEventReminder: settings.defaultEventReminder,
      defaultEventStatus: settings.defaultEventStatus,
      defaultEventColor: settings.defaultEventColor,
      showConflictWarnings: settings.showConflictWarnings,
      allowDoubleBooking: settings.allowDoubleBooking,
      timeFormat: settings.timeFormat,
      minBreakBetweenEvents: settings.minBreakBetweenEvents,
    }),
    [
      settings.defaultCalendarId,
      settings.defaultEventDuration,
      settings.defaultEventReminder,
      settings.defaultEventStatus,
      settings.defaultEventColor,
      settings.showConflictWarnings,
      settings.allowDoubleBooking,
      settings.timeFormat,
      settings.minBreakBetweenEvents,
      calendars,
    ],
  );

  const handleNewEventFromTemplate = useCallback(
    (template: import('@/lib/api').CalendarEventTemplateDetail) => {
      const now = new Date();
      const end = new Date(now.getTime() + (template.durationMinutes || 60) * 60000);
      setInitialEventData({
        title: template.title,
        description: template.description || undefined,
        location: template.location || undefined,
        startAt: now.toISOString(),
        endAt: end.toISOString(),
        isAllDay: template.isAllDay,
        status: template.status,
        color: template.color,
        url: template.url || undefined,
        calendarId: template.calendarId || undefined,
        isRecurring: template.isRecurring,
        rrule: template.rrule || undefined,
      });
      setEditEvent(null);
      setEventDialogOpen(true);
    },
    [],
  );

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <CalendarToolbar
        currentDate={currentDate}
        view={view}
        onViewChange={handleViewChange}
        onPrev={handlePrev}
        onNext={handleNext}
        onToday={handleToday}
        onNewEvent={handleNewEvent}
        onImport={() => setImportDialogOpen(true)}
        onSettings={() => setSettingsOpen(true)}
        onFindTime={() => setFindTimeOpen(true)}
        onSchedulingLinks={() => setSchedulingLinksOpen(true)}
        onToggleTasks={handleToggleTaskPanel}
        showTaskPanel={settings.showTaskPanel}
        onDailyPlanning={() => setDailyPlanningOpen(true)}
        onEveningReview={
          settings.eveningReviewEnabled ? () => setEveningReviewOpen(true) : undefined
        }
        onFocusTime={() => setFocusTimeDialogOpen(true)}
        searchQuery={search.query}
        onSearchQueryChange={search.setQuery}
        searchResults={search.results}
        searchLoading={search.loading}
        onSearchSelect={handleSearchSelect}
        onSearchDeleteEvents={handleDeleteSearchEvents}
        onSearchClear={search.clear}
        timeFormat={settings.timeFormat}
        meetingWarning={meetingWarning}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        {!settings.sidebarCollapsed && (
          <CalendarSidebar
            currentDate={currentDate}
            selectedDate={currentDate}
            weekStartDay={settings.weekStartDay}
            calendars={calendars}
            onSelectDate={setCurrentDate}
            onToggleCalendar={toggleVisibility}
            onAddCalendar={() => setCalendarFormOpen(true)}
            onDeleteCalendar={handleDeleteCalendar}
            onShareCalendar={handleShareCalendar}
            sharedCalendars={sharedCalendars}
            subscriptions={subscriptions}
            onSubscribe={() => setSubscribeDialogOpen(true)}
            onRefreshSubscription={handleRefreshSubscription}
            onUnsubscribe={handleUnsubscribe}
            calendarSets={calendarSets}
            activeCalendarSetId={activeCalendarSetId}
            onActivateSet={activateSet}
            onDeactivateSet={deactivateSet}
            onManageSets={() => setCalendarSetManagerOpen(true)}
            taskPanel={
              settings.showTaskPanel ? (
                <TaskPanel
                  tasks={tasks}
                  onNewTask={() => {
                    setEditTaskTarget(null);
                    setTaskDialogOpen(true);
                  }}
                  onAutoSchedule={handleAutoSchedule}
                  onCompleteTask={handleCompleteTask}
                  onClickTask={handleClickTask}
                  taskSortOrder={settings.taskSortOrder}
                  showCompletedTasks={settings.showCompletedTasks}
                />
              ) : undefined
            }
          />
        )}

        {/* Main view */}
        <CalendarViewArea
          view={view}
          currentDate={currentDate}
          visibleEvents={visibleEvents}
          settings={settings}
          loading={loading}
          error={calendarsError}
          onClickDate={handleClickDate}
          onClickEvent={handleClickEvent}
          onClickSlot={handleClickSlot}
          resizingEventId={resizingEventId}
          resizeHeight={resizeHeight}
          onResizeStart={onResizeStart}
          weather={weather}
          hourHeightPx={hourHeightPx}
        />
      </div>

      {/* Dialogs */}
      <CalendarDialogs
        // Event dialog
        eventDialogOpen={eventDialogOpen}
        onCloseEventDialog={() => {
          setEventDialogOpen(false);
          setEditEvent(null);
        }}
        onSaveEvent={handleSaveEvent}
        calendars={calendars}
        editEvent={editEvent}
        initialEventData={initialEventData}
        templates={templates}
        onSaveAsTemplate={editEvent ? handleSaveAsTemplate : undefined}
        eventDialogSettings={eventDialogSettings}
        visibleEvents={visibleEvents}
        // Quick event dialog
        quickEventOpen={quickEventOpen}
        quickEventDate={quickEventDate}
        quickEventHour={quickEventHour}
        onCloseQuickEvent={() => setQuickEventOpen(false)}
        onQuickSave={handleQuickSave}
        onQuickExpand={handleQuickExpand}
        // Event detail dialog
        detailDialogOpen={detailDialogOpen}
        selectedEvent={selectedEvent}
        onCloseDetailDialog={() => {
          setDetailDialogOpen(false);
          setSelectedEvent(null);
        }}
        onEditEvent={handleEditEvent}
        onDeleteEvent={handleDeleteEvent}
        // Calendar form dialog
        calendarFormOpen={calendarFormOpen}
        onCloseCalendarForm={() => setCalendarFormOpen(false)}
        onSaveCalendar={handleSaveCalendar}
        // Import dialog
        importDialogOpen={importDialogOpen}
        onCloseImport={() => setImportDialogOpen(false)}
        onImportComplete={() => {
          refetchEvents();
          fetchCalendars();
        }}
        onCreateCalendar={() => setCalendarFormOpen(true)}
        // Settings sheet
        settingsOpen={settingsOpen}
        onCloseSettings={() => setSettingsOpen(false)}
        settings={settings}
        onUpdateSettings={updateSettings}
        focusTimeSettings={focusTimeSettings}
        onUpdateFocusTime={updateFocusTimeSettings}
        onGenerateFocusBlocks={() => handleFocusBlocksAction(generateFocusBlocks)}
        onRegenerateFocusBlocks={() => handleFocusBlocksAction(regenerateFocusBlocks)}
        onClearFocusBlocks={() => handleFocusBlocksAction(clearFocusBlocks)}
        // Command palette
        commandPaletteOpen={commandPaletteOpen}
        onCloseCommandPalette={() => setCommandPaletteOpen(false)}
        onNewEvent={handleNewEvent}
        onToday={handleToday}
        onPrev={handlePrev}
        onNext={handleNext}
        onViewChange={handleViewChange}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenImport={() => setImportDialogOpen(true)}
        onGoToDate={handleGoToDate}
        calendarSets={calendarSets}
        onActivateSet={activateSet}
        onNewEventFromTemplate={handleNewEventFromTemplate}
        onManageTemplates={() => setTemplateManagerOpen(true)}
        // Shortcut help
        shortcutHelpOpen={shortcutHelpOpen}
        onCloseShortcutHelp={() => setShortcutHelpOpen(false)}
        // Calendar set manager
        calendarSetManagerOpen={calendarSetManagerOpen}
        onCloseCalendarSetManager={() => setCalendarSetManagerOpen(false)}
        onCreateSet={createSet}
        onRenameSet={renameSet}
        onDeleteSet={deleteCalendarSet}
        onUpdateSetCalendars={updateSetCalendars}
        // Template manager
        templateManagerOpen={templateManagerOpen}
        onCloseTemplateManager={() => setTemplateManagerOpen(false)}
        onCreateTemplate={createTemplate}
        onUpdateTemplate={updateTemplate}
        onDeleteTemplate={deleteTemplate}
        // Share calendar dialog
        shareDialogOpen={shareDialogOpen}
        onCloseShareDialog={() => {
          setShareDialogOpen(false);
          setShareCalendarTarget(null);
        }}
        shareCalendarTarget={shareCalendarTarget}
        // Find time dialog
        findTimeOpen={findTimeOpen}
        onCloseFindTime={() => setFindTimeOpen(false)}
        onFindTimeSelectSlot={handleFindTimeSelectSlot}
        // Scheduling links manager
        schedulingLinksOpen={schedulingLinksOpen}
        onCloseSchedulingLinks={() => setSchedulingLinksOpen(false)}
        schedulingLinks={schedulingLinks}
        onCreateLink={createSchedulingLink}
        onUpdateLink={updateSchedulingLink}
        onDeleteLink={deleteSchedulingLink}
        onFetchBookings={fetchSchedulingLinkBookings}
        onCancelBooking={cancelSchedulingLinkBooking}
        // Task dialog
        taskDialogOpen={taskDialogOpen}
        onCloseTaskDialog={() => {
          setTaskDialogOpen(false);
          setEditTaskTarget(null);
        }}
        onSaveTask={handleSaveTask}
        editTaskTarget={editTaskTarget}
        // Schedule suggestions dialog
        suggestionsDialogOpen={suggestionsDialogOpen}
        onCloseSuggestions={() => setSuggestionsDialogOpen(false)}
        suggestionsTask={suggestionsTask}
        suggestions={suggestions}
        suggestionsLoading={suggestionsLoading}
        onAcceptSuggestion={handleAcceptSuggestion}
        // Daily planning wizard
        dailyPlanningOpen={dailyPlanningOpen}
        onCloseDailyPlanning={() => setDailyPlanningOpen(false)}
        dailyPlanning={dailyPlanning}
        onCompleteTask={handleCompleteTask}
        onScheduleTaskToTime={handleScheduleTaskToTime}
        // Subscribe dialog
        subscribeDialogOpen={subscribeDialogOpen}
        onCloseSubscribe={() => setSubscribeDialogOpen(false)}
        onSubscribe={handleSubscribe}
        // Evening review wizard
        eveningReviewOpen={eveningReviewOpen}
        onCloseEveningReview={() => {
          dailyPlanning.dismissEveningReview();
          setEveningReviewOpen(false);
        }}
        eveningReviewDailyPlanning={dailyPlanning}
        tasks={tasks}
        // Focus time dialog
        focusTimeDialogOpen={focusTimeDialogOpen}
        onCloseFocusTimeDialog={() => setFocusTimeDialogOpen(false)}
        onOpenSettingsFromFocusTime={() => setSettingsOpen(true)}
      />
    </div>
  );
}
